











import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { runPayrollApi, getPayrollRecordsApi, rerunPayrollApi, approvePayrollApi, markPayrollPaidApi, exportPayrollReportApi, getPayslipApi, getAllPayslipsApi } from "../../app/api";
import PayrollKPIs from "./components/PayrollKPIs";
import PayslipModal from "./components/PayslipModal";
import { toast } from "react-hot-toast";
import {
  Users,
  Wallet,
  MinusCircle,
  Banknote,
  CreditCard,
  FileText,
  History,
  CheckCircle2,
  Clock,
  Search,
  Filter,
  Eye,
  Edit,
  Download,
  ChevronRight,
  Play,
  Calendar,
  LayoutGrid,
  List,
  AlertCircle
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const Payroll = () => {
  const navigate = useNavigate();

  const previousMonth = moment().subtract(1, 'month').format("YYYY-MM");
  const [fromMonth, setFromMonth] = useState(previousMonth); // YYYY-MM
  const [toMonth, setToMonth] = useState(previousMonth); // YYYY-MM
  const [viewType, setViewType] = useState("grid"); // grid or list
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [showExistsModal, setShowExistsModal] = useState(false);
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoadingRecords(true);
      try {
        const res = await getPayrollRecordsApi(fromMonth, toMonth);
        if (res.success) {
          setRecords(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch payroll records", error);
      } finally {
        setLoadingRecords(false);
      }
    };
    fetchRecords();
  }, [fromMonth, toMonth, refreshTrigger]);

  const [isApproving, setIsApproving] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [payslipData, setPayslipData] = useState(null);
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [isFetchingPayslip, setIsFetchingPayslip] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [generatedPayslipsCount, setGeneratedPayslipsCount] = useState(0);

  useEffect(() => {
    const fetchPayslipsCount = async () => {
      try {
        const res = await getAllPayslipsApi();
        if (res.success) {
          const countForMonth = res.data.filter(p => p.month === fromMonth).length;
          setGeneratedPayslipsCount(countForMonth);
        }
      } catch (error) {
        console.error("Failed to fetch payslips count", error);
      }
    };
    fetchPayslipsCount();
  }, [fromMonth, refreshTrigger]);

  const handleRunPayroll = async () => {
    const isRerun = records.length > 0;
    try {
      setIsRunning(true);
      const res = isRerun ? await rerunPayrollApi({ month: fromMonth }) : await runPayrollApi({ month: fromMonth });
      if (res.success) {
        toast.success(`Payroll ${isRerun ? 're-run' : 'processed'} successfully!`);
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error(`Error ${isRerun ? 're-running' : 'running'} payroll:`, error);
      setShowExistsModal(true);
    } finally {
      setIsRunning(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      const res = await approvePayrollApi({ month: fromMonth });
      if (res.success) {
        toast.success("Payroll approved successfully!");
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      toast.error("Failed to approve payroll");
    } finally {
      setIsApproving(false);
    }
  };

  const handlePaid = async () => {
    try {
      setIsPaying(true);
      const res = await markPayrollPaidApi({ month: fromMonth });
      if (res.success) {
        toast.success("Payroll marked as paid!");
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      toast.error("Failed to mark as paid");
    } finally {
      setIsPaying(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportPayrollReportApi(fromMonth, toMonth);
      toast.success("Report downloaded successfully!");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewPayslip = async (userId) => {
    try {
      setIsFetchingPayslip(true);
      const dateObj = moment(fromMonth, "YYYY-MM");
      const monthName = dateObj.format("MMMM");
      const yearStr = dateObj.format("YYYY");

      const res = await getPayslipApi(userId, monthName, yearStr);
      if (res.success) {
        setPayslipData(res.data);
        setIsPayslipModalOpen(true);
      } else {
        toast.error("Failed to load payslip data");
      }
    } catch (error) {
      toast.error("Error loading payslip");
    } finally {
      setIsFetchingPayslip(false);
    }
  };

  const handleDownloadPayslip = async (userId) => {
    try {
      setDownloadingId(userId);
      const dateObj = moment(fromMonth, "YYYY-MM");
      const monthName = dateObj.format("MMMM");
      const yearStr = dateObj.format("YYYY");

      const res = await getPayslipApi(userId, monthName, yearStr);
      if (res.success) {
        // Open in a new printable window
        const win = window.open("", "_blank");
        const [year, month] = res.data.month.split("-");
        const monthName = new Date(year, month - 1).toLocaleString("default", { month: "long" });
        win.document.write(`
          <html><head><title>Payslip - ${res.data.name} - ${monthName} ${year}</title>
          <style>body{font-family:serif;padding:24px;color:#000} table{width:100%;border-collapse:collapse} td,th{border:1px solid #000;padding:6px 8px} .right{text-align:right}</style></head>
          <body>
            <h2 style="text-align:center;text-decoration:underline">Salary Slip</h2>
            <p><b>Name:</b> ${res.data.name} &nbsp;&nbsp; <b>Month:</b> ${monthName} ${year}</p>
            <table>
              <tr><th>Earnings</th><th class='right'>Amount</th><th>Deductions</th><th class='right'>Amount</th></tr>
              <tr><td>Basic & DA</td><td class='right'>${Number(res.data.salary || 0).toFixed(2)}</td><td>Provident Fund</td><td class='right'>0.00</td></tr>
              <tr><td>HRA / Other</td><td class='right'>${Number(res.data.earnings || 0).toFixed(2)}</td><td>ESI</td><td class='right'>${Number(res.data.esi || 0).toFixed(2)}</td></tr>
              <tr><td colspan='2'></td><td>LWOP</td><td class='right'>${Number(res.data.leave_deduction || 0).toFixed(2)}</td></tr>
              <tr><td><b>Total Earnings</b></td><td class='right'><b>${Number(res.data.gross_salary || 0).toFixed(2)}</b></td><td><b>Total Deductions</b></td><td class='right'><b>${Number(res.data.total_deduction || 0).toFixed(2)}</b></td></tr>
              <tr><td colspan='2'></td><td><b>NET Salary</b></td><td class='right'><b>${Number(res.data.net_salary || 0).toFixed(2)}</b></td></tr>
            </table>
          </body></html>
        `);
        win.document.close();
        win.focus();
        win.print();
      } else {
        toast.error("Failed to load payslip for download");
      }
    } catch (error) {
      toast.error("Error downloading payslip");
    } finally {
      setDownloadingId(null);
    }
  };

  const approvedCount = records.filter(r => r.status === "approved" || r.status === "paid").length;
  const pendingCount = records.filter(r => r.status === "draft" || r.status === "pending").length;

  const secondaryKpis = [
    { label: "Approved", value: approvedCount, sub: "Ready to pay", icon: CheckCircle2, iconBg: "bg-[#22C55E]/20", iconColor: "text-[#22C55E]" },
    { label: "Pending", value: pendingCount, sub: "Awaiting approval", icon: Clock, iconBg: "bg-[#FF9800]/20", iconColor: "text-[#FF9800]" },
    { label: "Payslips Generated", value: generatedPayslipsCount, sub: "Total Payslips", icon: FileText, iconBg: "bg-[#0EA5E9]/20", iconColor: "text-[#0EA5E9]" },
  ];

  // Removed hardcoded dummy employees array, using actual API records instead

  const hasRecords = records.length > 0;

  const workflow = [
    {
      label: "Attendance Synced",
      sub: hasRecords ? "Data synced successfully" : "Pending attendance sync",
      status: hasRecords ? "completed" : "pending",
      step: 1
    },
    {
      label: "Salary Calculated",
      sub: hasRecords ? "Calculations completed" : "Waiting for sync",
      status: hasRecords ? "completed" : (hasRecords ? "pending" : "upcoming"),
      step: 2
    },
    {
      label: "Approval Pending",
      sub: (records.some(r => r.status === "approved" || r.status === "paid")) ? "Approved by HR/Admin" : (hasRecords ? "Waiting for approval" : "Pending calculation"),
      status: (records.some(r => r.status === "approved" || r.status === "paid")) ? "completed" : (hasRecords ? "pending" : "upcoming"),
      step: 3
    },
    {
      label: "Payslips Generate",
      sub: generatedPayslipsCount > 0 ? `${generatedPayslipsCount} Payslips Generated` : ((records.some(r => r.status === "approved" || r.status === "paid")) ? "Pending generation" : "Waiting for approval"),
      status: generatedPayslipsCount > 0 ? "completed" : ((records.some(r => r.status === "approved" || r.status === "paid")) ? "pending" : "upcoming"),
      step: 4
    },
  ];

  const quickReports = [
    { label: "Salary Report", icon: FileText, path: "/payroll/salaries" },
    { label: "Payroll Record", icon: FileText, path: "/payroll/records" },
    { label: "Payslips", icon: FileText, path: "/payroll/payslips" },
  ];

  const isPayrollApproved = records.some(r => r.status === "approved" || r.status === "paid");
  const isPayrollPaid = records.some(r => r.status === "paid");

  const filteredRecords = [...records].sort((a, b) => (a.NAME || "").localeCompare(b.NAME || "")).filter(rec =>
    rec.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rec.user_id?.toString().includes(searchTerm)
  );

  const recordsPerPage = 8;
  const processedRecords = useMemo(() => {
    if (fromMonth === toMonth) return filteredRecords;

    const grouped = new Map();
    filteredRecords.forEach(rec => {
      const key = rec.user_id;
      if (!grouped.has(key)) {
        grouped.set(key, { ...rec, allMonths: [rec.month] });
      } else {
        const existing = grouped.get(key);
        existing.gross_salary = Number(existing.gross_salary) + Number(rec.gross_salary);
        existing.total_deduction = Number(existing.total_deduction) + Number(rec.total_deduction);
        existing.hold_back_amount = Number(existing.hold_back_amount || 0) + Number(rec.hold_back_amount || 0);
        existing.net_salary = Number(existing.net_salary) + Number(rec.net_salary);
        if (!existing.allMonths.includes(rec.month)) {
          existing.allMonths.push(rec.month);
        }
        
        // Status priority: paid > approved > draft
        const statusOrder = { 'paid': 3, 'approved': 2, 'draft': 1 };
        if (statusOrder[rec.status] > statusOrder[existing.status]) {
          existing.status = rec.status;
        }
      }
    });

    return Array.from(grouped.values()).map(rec => {
      if (rec.allMonths.length > 1) {
        const sorted = [...rec.allMonths].sort();
        rec.month = `${sorted[0]} to ${sorted[sorted.length - 1]}`;
      }
      return rec;
    });
  }, [filteredRecords, fromMonth, toMonth]);

  const totalPages = Math.ceil(processedRecords.length / recordsPerPage) || 1;
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = processedRecords.slice(indexOfFirstRecord, indexOfLastRecord);

  const totalNet = records.reduce((acc, r) => acc + Number(r.net_salary || 0), 0);
  const totalDeductions = records.reduce((acc, r) => acc + Number(r.total_deduction || 0), 0);
  const pieData = [
    { name: 'Net Salary', value: totalNet, color: '#22C55E' },
    { name: 'Deductions', value: totalDeductions, color: '#EF4444' }
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row gap-6">

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 space-y-6">

          {/* HEADER */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                PAYROLL <span className="text-white/70 font-normal">| {new Date(fromMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}{fromMonth !== toMonth && toMonth && ` - ${new Date(toMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}`}</span>
              </h1>
              <p className="text-white/80 text-sm">Salary processing & employee payouts</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleRunPayroll}
                disabled={isRunning || isPayrollApproved}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition disabled:opacity-50"
              >
                {records.length > 0 ? (
                  <>
                    <History size={16} className={isRunning ? "animate-spin" : ""} />
                    {isRunning ? "Re-running..." : "Rerun Payroll"}
                  </>
                ) : (
                  <>
                    <Play size={16} fill="currentColor" className={isRunning ? "animate-pulse" : ""} />
                    {isRunning ? "Processing..." : "Process Payroll"}
                  </>
                )}
              </button>
              <button
                onClick={() => navigate("/payroll/payslips")}
                className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10">
                <FileText size={16} /> Generate Payslips
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10 disabled:opacity-50"
              >
                {isExporting ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Download size={16} />}
                Export Report
              </button>
            </div>
          </div>

          {/* MAIN KPI CARDS */}
          <PayrollKPIs fromMonth={fromMonth} toMonth={toMonth} refreshTrigger={refreshTrigger} />

          {/* SECONDARY KPI CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {secondaryKpis.map((kpi, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-4 rounded-2xl flex items-center gap-4">
                <div className={`${kpi.iconBg} p-2.5 rounded-lg`}>
                  <kpi.icon className={kpi.iconColor} size={20} />
                </div>
                <div>
                  <p className="text-white/70 text-[10px] uppercase tracking-wider font-semibold">{kpi.label}</p>
                  <div className="flex items-baseline gap-2">
                    <h4 className="text-lg font-bold">{kpi.value}</h4>
                    <span className="text-white/30 text-[10px]">{kpi.sub}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-1 flex flex-col md:flex-row items-center gap-6 w-full">
              {/* Date Range Picker Group */}
              <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2 shadow-inner">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">FROM</span>
                  <DatePicker
                    selected={fromMonth ? new Date(fromMonth + "-01") : null}
                    onChange={(date) => {
                      setFromMonth(date ? moment(date).format("YYYY-MM") : "");
                    }}
                    dateFormat="MMMM, yyyy"
                    showMonthYearPicker
                    maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
                    className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
                  />
                </div>
                <div className="w-[1px] h-6 bg-white/10" />
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">TO</span>
                  <DatePicker
                    selected={toMonth ? new Date(toMonth + "-01") : null}
                    onChange={(date) => {
                      setToMonth(date ? moment(date).format("YYYY-MM") : "");
                    }}
                    dateFormat="MMMM, yyyy"
                    showMonthYearPicker
                    maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
                    className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
                  />
                </div>
              </div>


              {/* Search Bar */}
              <div className="flex-1 relative w-full">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search employee by name or ID..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500/50 transition shadow-inner"
                />
              </div>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition"
              title="Refresh page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
              Refresh
            </button>
          </div>

          {/* EMPLOYEE PAYROLL LIST */}
          <div className="space-y-4">
            {/* Title Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-bold">Employee Payroll</h2>
                <Link to="/payroll/salaries" className="bg-purple-500/20 text-purple-400 text-[10px] px-2 py-0.5 rounded-full font-bold hover:bg-purple-500/30 transition">
                  View All Salaries
                </Link>
              </div>
              {/* View toggle */}
              <div className="flex bg-white/5 border border-white/10 rounded-lg p-1 self-start sm:self-auto">
                <button
                  onClick={() => setViewType("grid")}
                  className={`p-1.5 rounded ${viewType === "grid" ? "bg-white/10 text-white" : "text-white/70 hover:text-white"}`}
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  onClick={() => setViewType("list")}
                  className={`p-1.5 rounded ${viewType === "list" ? "bg-white/10 text-white" : "text-white/70 hover:text-white"}`}
                >
                  <List size={16} />
                </button>
              </div>
            </div>

            {/* Approve / Mark Paid action bar */}
            {records.length > 0 && (
              <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <span className="text-white/70 text-xs font-semibold uppercase tracking-wider mr-1">Actions</span>
                <button
                  onClick={handleApprove}
                  disabled={isApproving || isPayrollApproved}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
                >
                  {isApproving ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={14} />}
                  Approve Payroll
                </button>
                <button
                  onClick={handlePaid}
                  disabled={isPaying || isPayrollPaid}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
                >
                  {isPaying ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Banknote size={14} />}
                  Mark Paid
                </button>
              </div>
            )}

            {loadingRecords ? (
              <div className="col-span-full flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : processedRecords.length > 0 ? (
              viewType === "list" ? (
                <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Employee</th>
                        <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Gross Salary</th>
                        <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Deductions</th>
                        <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Net Salary</th>
                        <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Status</th>
                        <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {currentRecords.map((rec) => (
                        <tr key={rec.id} className="hover:bg-white/5 transition group">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center font-bold border border-white/10 uppercase text-xs">
                                {(rec.NAME || "U")[0]}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-white">{rec.NAME}</p>
                                {/* <p className="text-[10px] text-white/70">ID: {rec.user_id}</p> */}
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm font-medium text-right">₹{Number(rec.gross_salary).toLocaleString()}</td>
                          <td className="p-4 text-sm font-medium text-right text-red-400">-₹{Number(rec.total_deduction).toLocaleString()}</td>
                          <td className="p-4 text-sm font-black text-right text-green-400">₹{Number(rec.net_salary).toLocaleString()}</td>
                          <td className="p-4 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${rec.status === "draft" ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/20" :
                              rec.status === "approved" ? "bg-blue-500/20 text-blue-400 border border-blue-500/20" :
                                "bg-green-500/20 text-green-400 border border-green-500/20"
                              }`}>
                              {rec.status}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => handleViewPayslip(rec.user_id)}
                              disabled={isFetchingPayslip}
                              className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition disabled:opacity-50"
                              title="View Payslip"
                            >
                              <FileText size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {currentRecords.map((rec) => {
                    const isMaturityMonth = Number(rec.net_salary) > (Number(rec.gross_salary) - Number(rec.total_deduction));

                    return (
                      <div key={rec.id} className={`bg-white/5 border border-white/10 p-5 rounded-2xl space-y-4 hover:border-purple-500/30 transition group relative ${isMaturityMonth ? 'ring-2 ring-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.4)]' : ''}`}>
                        {isMaturityMonth && (
                          <div className="absolute -top-3 -right-3 bg-purple-500 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg animate-bounce z-10">
                            MATURITY RELEASED!
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center font-bold border-2 border-white/10 uppercase shrink-0">
                              {(rec.NAME || "U")[0]}
                            </div>
                            <div>
                              <h4 className="text-sm font-bold truncate max-w-[100px]">{rec.NAME}</h4>
                              <span className={`inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${rec.status === "draft" ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/20" :
                                rec.status === "approved" ? "bg-blue-500/20 text-blue-400 border border-blue-500/20" :
                                  "bg-green-500/20 text-green-400 border border-green-500/20"
                                }`}>
                                {rec.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 py-2 border-y border-white/5">
                          <div className="flex justify-between text-[11px]">
                            <span className="text-white/70">Gross Salary</span>
                            <span className="font-medium">₹{Number(rec.gross_salary).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-[11px]">
                            <span className="text-white/70">Deductions</span>
                            <span className="font-medium text-red-400">-₹{Number(rec.total_deduction).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-[11px]">
                            <span className="text-white/70">Hold-back</span>
                            <span className="font-medium text-amber-400">-₹{Number(rec.live_hold_back_amount != null ? rec.live_hold_back_amount : rec.hold_back_amount || 0).toLocaleString()}</span>
                          </div>
                          {isMaturityMonth && (
                            <div className="flex justify-between text-[11px] bg-purple-500/10 p-1 rounded">
                              <span className="text-purple-300 font-bold">Released Amount</span>
                              <span className="font-black text-purple-300">+₹{(Number(rec.net_salary) - (Number(rec.gross_salary) - Number(rec.total_deduction) - Number(rec.live_hold_back_amount != null ? rec.live_hold_back_amount : rec.hold_back_amount || 0))).toLocaleString()}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-between items-center pt-1">
                          <div>
                            <p className="text-[10px] text-white/30 uppercase font-bold">Net Payable</p>
                            <p className="text-lg font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                            <button
                              onClick={() => handleViewPayslip(rec.user_id)}
                              disabled={isFetchingPayslip}
                              className="p-2 bg-white/5 hover:bg-purple-500/20 hover:text-purple-300 rounded-lg text-white/70 transition disabled:opacity-50"
                              title="View Payslip"
                            >
                              <Eye size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="col-span-full bg-white/5 border border-white/10 p-10 rounded-2xl text-center">
                <History size={32} className="text-white/20 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-white mb-1">No Payroll Records Found</h3>
                <p className="text-xs text-white/70">Try adjusting your search or processing payroll.</p>
              </div>
            )}

            {/* PAGINATION */}
            {processedRecords.length > 0 && (
              <div className="flex items-center justify-center gap-2 py-4">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
                >
                  <ChevronRight size={18} className="rotate-180" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
                      ? 'bg-purple-600 text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="w-full lg:w-80 space-y-6">

          {/* WORKFLOW */}
          <div className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-6">
            <h3 className="text-lg font-bold">Payroll Workflow</h3>
            <div className="space-y-8 relative">
              {/* Connector Line */}
              <div className="absolute left-3.5 top-2 bottom-2 w-0.5 bg-white/10" />

              {workflow.map((item, i) => (
                <div key={i} className="flex gap-4 relative z-10">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${item.status === "completed" ? "bg-green-500 text-white" :
                    item.status === "pending" ? "bg-purple-600 text-white" :
                      "bg-white/10 text-white/70 border border-white/10"
                    }`}>
                    {item.status === "completed" ? <CheckCircle2 size={16} /> : <span className="text-xs font-bold">{item.step || i + 1}</span>}
                  </div>
                  <div>
                    <h4 className={`text-sm font-bold ${item.status === "upcoming" ? "text-white/70" : "text-white"}`}>
                      {item.label}
                    </h4>
                    <p className="text-[11px] text-white/30">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* QUICK REPORTS */}
          <div className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-6">
            <h3 className="text-lg font-bold">Quick Reports</h3>
            <div className="space-y-3">
              {quickReports.map((report, i) => (
                <button
                  key={i}
                  onClick={() => report.path && navigate(report.path)}
                  className="w-full bg-white/5 border border-white/5 p-3 rounded-xl flex items-center justify-between hover:bg-white/10 transition group"
                >
                  <div className="flex items-center gap-3">
                    <report.icon className="text-white/70 group-hover:text-white/70 transition" size={18} />
                    <span className="text-xs text-white/70 group-hover:text-white transition">{report.label}</span>
                  </div>
                  <ChevronRight size={14} className="text-white/30" />
                </button>
              ))}
            </div>
          </div>

          {/* PAYROLL DISTRIBUTION PIE CHART */}
          <div className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-4">
            <h3 className="text-lg font-bold">Payroll Distribution</h3>
            {totalNet > 0 || totalDeductions > 0 ? (
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `₹${value.toLocaleString()}`}
                      contentStyle={{ backgroundColor: '#1B103F', borderColor: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-[10px] font-bold uppercase tracking-wider text-white/60">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" /> Net Salary
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" /> Deductions
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-white/30 text-sm">
                No payroll data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ALREADY EXISTS MODAL */}
      {showExistsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowExistsModal(false)} />
          <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-md rounded-3xl p-8 shadow-2xl space-y-6 text-center transform transition-all">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={40} className="text-yellow-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-white">Payroll Already Processed</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Payroll for <span className="text-white font-bold">{new Date(fromMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span> has already been generated and processed.
              </p>
            </div>
            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={() => setShowExistsModal(false)}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold transition border border-white/10"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowExistsModal(false);
                  navigate("/payroll/records");
                }}
                className="w-full text-purple-400 text-sm font-bold hover:text-purple-300 transition"
              >
                View Processed Records
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAYSLIP MODAL */}
      <PayslipModal
        isOpen={isPayslipModalOpen}
        onClose={() => setIsPayslipModalOpen(false)}
        data={payslipData}
      />
    </div>
  );
};

export default Payroll;






// import React, { useState } from "react";
// import { useNavigate, Link } from "react-router-dom";
// import { runPayrollApi, getPayrollRecordsApi, rerunPayrollApi, approvePayrollApi, markPayrollPaidApi, exportPayrollReportApi, getPayslipApi, getAllPayslipsApi } from "../../app/api";
// import PayrollKPIs from "./components/PayrollKPIs";
// import PayslipModal from "./components/PayslipModal";
// import { toast } from "react-hot-toast";
// import {
//   Users,
//   Wallet,
//   MinusCircle,
//   Banknote,
//   CreditCard,
//   FileText,
//   History,
//   CheckCircle2,
//   Clock,
//   Search,
//   Filter,
//   Eye,
//   Edit,
//   Download,
//   ChevronRight,
//   Play,
//   Calendar,
//   LayoutGrid,
//   List,
//   AlertCircle
// } from "lucide-react";
// import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import moment from "moment";

// const Payroll = () => {
//   const navigate = useNavigate();

//   const currentMonth = new Date().toISOString().slice(0, 7);
//   const [fromMonth, setFromMonth] = useState(currentMonth); // YYYY-MM
//   const [toMonth, setToMonth] = useState(currentMonth); // YYYY-MM
//   const [viewType, setViewType] = useState("grid"); // grid or list
//   const [refreshTrigger, setRefreshTrigger] = useState(0);
//   const [isRunning, setIsRunning] = useState(false);
//   const [showExistsModal, setShowExistsModal] = useState(false);
//   const [records, setRecords] = useState([]);
//   const [loadingRecords, setLoadingRecords] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);

//   React.useEffect(() => {
//     const fetchRecords = async () => {
//       setLoadingRecords(true);
//       try {
//         const res = await getPayrollRecordsApi(fromMonth, toMonth);
//         if (res.success) {
//           setRecords(res.data);
//         }
//       } catch (error) {
//         console.error("Failed to fetch payroll records", error);
//       } finally {
//         setLoadingRecords(false);
//       }
//     };
//     fetchRecords();
//   }, [fromMonth, toMonth, refreshTrigger]);

//   const [isApproving, setIsApproving] = useState(false);
//   const [isPaying, setIsPaying] = useState(false);
//   const [isExporting, setIsExporting] = useState(false);

//   const [payslipData, setPayslipData] = useState(null);
//   const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
//   const [isFetchingPayslip, setIsFetchingPayslip] = useState(false);
//   const [downloadingId, setDownloadingId] = useState(null);
//   const [generatedPayslipsCount, setGeneratedPayslipsCount] = useState(0);

//   React.useEffect(() => {
//     const fetchPayslipsCount = async () => {
//       try {
//         const res = await getAllPayslipsApi();
//         if (res.success) {
//           const countForMonth = res.data.filter(p => p.month === fromMonth).length;
//           setGeneratedPayslipsCount(countForMonth);
//         }
//       } catch (error) {
//         console.error("Failed to fetch payslips count", error);
//       }
//     };
//     fetchPayslipsCount();
//   }, [fromMonth, refreshTrigger]);

//   const handleRunPayroll = async () => {
//     const isRerun = records.length > 0;
//     try {
//       setIsRunning(true);
//       const res = isRerun ? await rerunPayrollApi({ month: fromMonth }) : await runPayrollApi({ month: fromMonth });
//       if (res.success) {
//         toast.success(`Payroll ${isRerun ? 're-run' : 'processed'} successfully!`);
//         setRefreshTrigger(prev => prev + 1);
//       }
//     } catch (error) {
//       console.error(`Error ${isRerun ? 're-running' : 'running'} payroll:`, error);
//       setShowExistsModal(true);
//     } finally {
//       setIsRunning(false);
//     }
//   };

//   const handleApprove = async () => {
//     try {
//       setIsApproving(true);
//       const res = await approvePayrollApi({ month: fromMonth });
//       if (res.success) {
//         toast.success("Payroll approved successfully!");
//         setRefreshTrigger(prev => prev + 1);
//       }
//     } catch (error) {
//       toast.error("Failed to approve payroll");
//     } finally {
//       setIsApproving(false);
//     }
//   };

//   const handlePaid = async () => {
//     try {
//       setIsPaying(true);
//       const res = await markPayrollPaidApi({ month: fromMonth });
//       if (res.success) {
//         toast.success("Payroll marked as paid!");
//         setRefreshTrigger(prev => prev + 1);
//       }
//     } catch (error) {
//       toast.error("Failed to mark as paid");
//     } finally {
//       setIsPaying(false);
//     }
//   };

//   const handleExport = async () => {
//     try {
//       setIsExporting(true);
//       await exportPayrollReportApi(fromMonth, toMonth);
//       toast.success("Report downloaded successfully!");
//     } catch (error) {
//       toast.error("Failed to export report");
//     } finally {
//       setIsExporting(false);
//     }
//   };

//   const handleViewPayslip = async (userId) => {
//     try {
//       setIsFetchingPayslip(true);
//       const dateObj = moment(fromMonth, "YYYY-MM");
//       const monthName = dateObj.format("MMMM");
//       const yearStr = dateObj.format("YYYY");

//       const res = await getPayslipApi(userId, monthName, yearStr);
//       if (res.success) {
//         setPayslipData(res.data);
//         setIsPayslipModalOpen(true);
//       } else {
//         toast.error("Failed to load payslip data");
//       }
//     } catch (error) {
//       toast.error("Error loading payslip");
//     } finally {
//       setIsFetchingPayslip(false);
//     }
//   };

//   const handleDownloadPayslip = async (userId) => {
//     try {
//       setDownloadingId(userId);
//       const dateObj = moment(fromMonth, "YYYY-MM");
//       const monthName = dateObj.format("MMMM");
//       const yearStr = dateObj.format("YYYY");

//       const res = await getPayslipApi(userId, monthName, yearStr);
//       if (res.success) {
//         // Open in a new printable window
//         const win = window.open("", "_blank");
//         const [year, month] = res.data.month.split("-");
//         const monthName = new Date(year, month - 1).toLocaleString("default", { month: "long" });
//         win.document.write(`
//           <html><head><title>Payslip - ${res.data.name} - ${monthName} ${year}</title>
//           <style>body{font-family:serif;padding:24px;color:#000} table{width:100%;border-collapse:collapse} td,th{border:1px solid #000;padding:6px 8px} .right{text-align:right}</style></head>
//           <body>
//             <h2 style="text-align:center;text-decoration:underline">Salary Slip</h2>
//             <p><b>Name:</b> ${res.data.name} &nbsp;&nbsp; <b>Month:</b> ${monthName} ${year}</p>
//             <table>
//               <tr><th>Earnings</th><th class='right'>Amount</th><th>Deductions</th><th class='right'>Amount</th></tr>
//               <tr><td>Basic & DA</td><td class='right'>${Number(res.data.salary || 0).toFixed(2)}</td><td>Provident Fund</td><td class='right'>0.00</td></tr>
//               <tr><td>HRA / Other</td><td class='right'>${Number(res.data.earnings || 0).toFixed(2)}</td><td>ESI</td><td class='right'>${Number(res.data.esi || 0).toFixed(2)}</td></tr>
//               <tr><td colspan='2'></td><td>LWOP</td><td class='right'>${Number(res.data.leave_deduction || 0).toFixed(2)}</td></tr>
//               <tr><td><b>Total Earnings</b></td><td class='right'><b>${Number(res.data.gross_salary || 0).toFixed(2)}</b></td><td><b>Total Deductions</b></td><td class='right'><b>${Number(res.data.total_deduction || 0).toFixed(2)}</b></td></tr>
//               <tr><td colspan='2'></td><td><b>NET Salary</b></td><td class='right'><b>${Number(res.data.net_salary || 0).toFixed(2)}</b></td></tr>
//             </table>
//           </body></html>
//         `);
//         win.document.close();
//         win.focus();
//         win.print();
//       } else {
//         toast.error("Failed to load payslip for download");
//       }
//     } catch (error) {
//       toast.error("Error downloading payslip");
//     } finally {
//       setDownloadingId(null);
//     }
//   };

//   const approvedCount = records.filter(r => r.status === "approved" || r.status === "paid").length;
//   const pendingCount = records.filter(r => r.status === "draft" || r.status === "pending").length;

//   const secondaryKpis = [
//     { label: "Approved", value: approvedCount, sub: "Ready to pay", icon: CheckCircle2, iconBg: "bg-[#22C55E]/20", iconColor: "text-[#22C55E]" },
//     { label: "Pending", value: pendingCount, sub: "Awaiting approval", icon: Clock, iconBg: "bg-[#FF9800]/20", iconColor: "text-[#FF9800]" },
//     { label: "Payslips Generated", value: generatedPayslipsCount, sub: "Total Payslips", icon: FileText, iconBg: "bg-[#0EA5E9]/20", iconColor: "text-[#0EA5E9]" },
//   ];

//   // Removed hardcoded dummy employees array, using actual API records instead

//   const hasRecords = records.length > 0;

//   const workflow = [
//     {
//       label: "Attendance Synced",
//       sub: hasRecords ? "Data synced successfully" : "Pending attendance sync",
//       status: hasRecords ? "completed" : "pending",
//       step: 1
//     },
//     {
//       label: "Salary Calculated",
//       sub: hasRecords ? "Calculations completed" : "Waiting for sync",
//       status: hasRecords ? "completed" : (hasRecords ? "pending" : "upcoming"),
//       step: 2
//     },
//     {
//       label: "Approval Pending",
//       sub: (records.some(r => r.status === "approved" || r.status === "paid")) ? "Approved by HR/Admin" : (hasRecords ? "Waiting for approval" : "Pending calculation"),
//       status: (records.some(r => r.status === "approved" || r.status === "paid")) ? "completed" : (hasRecords ? "pending" : "upcoming"),
//       step: 3
//     },
//     {
//       label: "Payslips Generate",
//       sub: generatedPayslipsCount > 0 ? `${generatedPayslipsCount} Payslips Generated` : ((records.some(r => r.status === "approved" || r.status === "paid")) ? "Pending generation" : "Waiting for approval"),
//       status: generatedPayslipsCount > 0 ? "completed" : ((records.some(r => r.status === "approved" || r.status === "paid")) ? "pending" : "upcoming"),
//       step: 4
//     },
//   ];

//   const quickReports = [
//     { label: "Salary Report", icon: FileText, path: "/payroll/salaries" },
//     { label: "Payroll Record", icon: FileText, path: "/payroll/records" },
//     { label: "Payslips", icon: FileText, path: "/payroll/payslips" },
//   ];

//   const isPayrollApproved = records.some(r => r.status === "approved" || r.status === "paid");
//   const isPayrollPaid = records.some(r => r.status === "paid");

//   const filteredRecords = records.filter(rec =>
//     rec.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//     rec.user_id?.toString().includes(searchTerm)
//   );

//   const recordsPerPage = 8;
//   const totalPages = Math.ceil(filteredRecords.length / recordsPerPage) || 1;
//   const indexOfLastRecord = currentPage * recordsPerPage;
//   const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
//   const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);

//   const totalNet = records.reduce((acc, r) => acc + Number(r.net_salary || 0), 0);
//   const totalDeductions = records.reduce((acc, r) => acc + Number(r.total_deduction || 0), 0);
//   const pieData = [
//     { name: 'Net Salary', value: totalNet, color: '#22C55E' },
//     { name: 'Deductions', value: totalDeductions, color: '#EF4444' }
//   ];

//   React.useEffect(() => {
//     setCurrentPage(1);
//   }, [searchTerm]);

//   return (
//     <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
//       <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row gap-6">

//         {/* MAIN CONTENT AREA */}
//         <div className="flex-1 space-y-6">

//           {/* HEADER */}
//           <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
//             <div>
//               <h1 className="text-2xl font-bold flex items-center gap-2">
//                 PAYROLL <span className="text-white/70 font-normal">| {new Date(fromMonth).toLocaleString('default', { month: 'long', year: 'numeric' })} {toMonth && `- ${new Date(toMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}`}</span>
//               </h1>
//               <p className="text-white/80 text-sm">Salary processing & employee payouts</p>
//             </div>
//             <div className="flex flex-wrap items-center gap-3">
//               <button
//                 onClick={handleRunPayroll}
//                 disabled={isRunning || isPayrollApproved}
//                 className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition disabled:opacity-50"
//               >
//                 {records.length > 0 ? (
//                   <>
//                     <History size={16} className={isRunning ? "animate-spin" : ""} />
//                     {isRunning ? "Re-running..." : "Rerun Payroll"}
//                   </>
//                 ) : (
//                   <>
//                     <Play size={16} fill="currentColor" className={isRunning ? "animate-pulse" : ""} />
//                     {isRunning ? "Processing..." : "Process Payroll"}
//                   </>
//                 )}
//               </button>
//               <button
//                 onClick={() => navigate("/payroll/payslips")}
//                 className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10">
//                 <FileText size={16} /> Generate Payslips
//               </button>
//               <button
//                 onClick={handleExport}
//                 disabled={isExporting}
//                 className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10 disabled:opacity-50"
//               >
//                 {isExporting ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Download size={16} />}
//                 Export Report
//               </button>
//             </div>
//           </div>

//           {/* MAIN KPI CARDS */}
//           <PayrollKPIs fromMonth={fromMonth} toMonth={toMonth} refreshTrigger={refreshTrigger} />

//           {/* SECONDARY KPI CARDS */}
//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
//             {secondaryKpis.map((kpi, i) => (
//               <div key={i} className="bg-white/5 border border-white/10 p-4 rounded-2xl flex items-center gap-4">
//                 <div className={`${kpi.iconBg} p-2.5 rounded-lg`}>
//                   <kpi.icon className={kpi.iconColor} size={20} />
//                 </div>
//                 <div>
//                   <p className="text-white/70 text-[10px] uppercase tracking-wider font-semibold">{kpi.label}</p>
//                   <div className="flex items-baseline gap-2">
//                     <h4 className="text-lg font-bold">{kpi.value}</h4>
//                     <span className="text-white/30 text-[10px]">{kpi.sub}</span>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col lg:flex-row items-center gap-6">
//             <div className="flex-1 flex flex-col md:flex-row items-center gap-6 w-full">
//               {/* Date Range Picker Group */}
//               <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2 shadow-inner">
//                 <div className="flex items-center gap-3">
//                   <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">FROM</span>
//                   <DatePicker
//                     selected={fromMonth ? new Date(fromMonth + "-01") : null}
//                     onChange={(date) => {
//                       setFromMonth(date ? moment(date).format("YYYY-MM") : "");
//                     }}
//                     dateFormat="MMMM, yyyy"
//                     showMonthYearPicker
//                     className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
//                   />
//                 </div>
//                 <div className="w-[1px] h-6 bg-white/10" />
//                 <div className="flex items-center gap-3">
//                   <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">TO</span>
//                   <DatePicker
//                     selected={toMonth ? new Date(toMonth + "-01") : null}
//                     onChange={(date) => {
//                       setToMonth(date ? moment(date).format("YYYY-MM") : "");
//                     }}
//                     dateFormat="MMMM, yyyy"
//                     showMonthYearPicker
//                     className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
//                   />
//                 </div>
//               </div>


//               {/* Search Bar */}
//               <div className="flex-1 relative w-full">
//                 <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
//                 <input
//                   type="text"
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                   placeholder="Search employee by name or ID..."
//                   className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500/50 transition shadow-inner"
//                 />
//               </div>
//             </div>

//             <button
//               onClick={() => window.location.reload()}
//               className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition"
//               title="Refresh page"
//             >
//               <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
//               Refresh
//             </button>
//           </div>

//           {/* EMPLOYEE PAYROLL LIST */}
//           <div className="space-y-4">
//             {/* Title Row */}
//             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
//               <div className="flex items-center gap-3 flex-wrap">
//                 <h2 className="text-lg font-bold">Employee Payroll</h2>
//                 <Link to="/payroll/salaries" className="bg-purple-500/20 text-purple-400 text-[10px] px-2 py-0.5 rounded-full font-bold hover:bg-purple-500/30 transition">
//                   View All Salaries
//                 </Link>
//               </div>
//               {/* View toggle */}
//               <div className="flex bg-white/5 border border-white/10 rounded-lg p-1 self-start sm:self-auto">
//                 <button
//                   onClick={() => setViewType("grid")}
//                   className={`p-1.5 rounded ${viewType === "grid" ? "bg-white/10 text-white" : "text-white/70 hover:text-white"}`}
//                 >
//                   <LayoutGrid size={16} />
//                 </button>
//                 <button
//                   onClick={() => setViewType("list")}
//                   className={`p-1.5 rounded ${viewType === "list" ? "bg-white/10 text-white" : "text-white/70 hover:text-white"}`}
//                 >
//                   <List size={16} />
//                 </button>
//               </div>
//             </div>

//             {/* Approve / Mark Paid action bar */}
//             {records.length > 0 && (
//               <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
//                 <span className="text-white/70 text-xs font-semibold uppercase tracking-wider mr-1">Actions</span>
//                 <button
//                   onClick={handleApprove}
//                   disabled={isApproving || isPayrollApproved}
//                   className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
//                 >
//                   {isApproving ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={14} />}
//                   Approve Payroll
//                 </button>
//                 <button
//                   onClick={handlePaid}
//                   disabled={isPaying || isPayrollPaid}
//                   className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
//                 >
//                   {isPaying ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Banknote size={14} />}
//                   Mark Paid
//                 </button>
//               </div>
//             )}

//             {loadingRecords ? (
//               <div className="col-span-full flex justify-center py-10">
//                 <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
//               </div>
//             ) : filteredRecords.length > 0 ? (
//               viewType === "list" ? (
//                 <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
//                   <table className="w-full text-left border-collapse min-w-[800px]">
//                     <thead>
//                       <tr className="border-b border-white/10 bg-white/5">
//                         <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Employee</th>
//                         <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Gross Salary</th>
//                         <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Deductions</th>
//                         <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Net Salary</th>
//                         <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Status</th>
//                         <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
//                       </tr>
//                     </thead>
//                     <tbody className="divide-y divide-white/5">
//                       {currentRecords.map((rec) => (
//                         <tr key={rec.id} className="hover:bg-white/5 transition group">
//                           <td className="p-4">
//                             <div className="flex items-center gap-3">
//                               <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center font-bold border border-white/10 uppercase text-xs">
//                                 {(rec.NAME || "U")[0]}
//                               </div>
//                               <div>
//                                 <p className="text-sm font-semibold text-white">{rec.NAME}</p>
//                                 {/* <p className="text-[10px] text-white/70">ID: {rec.user_id}</p> */}
//                               </div>
//                             </div>
//                           </td>
//                           <td className="p-4 text-sm font-medium text-right">₹{Number(rec.gross_salary).toLocaleString()}</td>
//                           <td className="p-4 text-sm font-medium text-right text-red-400">-₹{Number(rec.total_deduction).toLocaleString()}</td>
//                           <td className="p-4 text-sm font-black text-right text-green-400">₹{Number(rec.net_salary).toLocaleString()}</td>
//                           <td className="p-4 text-center">
//                             <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${rec.status === "draft" ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/20" :
//                               rec.status === "approved" ? "bg-blue-500/20 text-blue-400 border border-blue-500/20" :
//                                 "bg-green-500/20 text-green-400 border border-green-500/20"
//                               }`}>
//                               {rec.status}
//                             </span>
//                           </td>
//                           <td className="p-4 text-center">
//                             <button
//                               onClick={() => handleViewPayslip(rec.user_id)}
//                               disabled={isFetchingPayslip}
//                               className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition disabled:opacity-50"
//                               title="View Payslip"
//                             >
//                               <FileText size={16} />
//                             </button>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               ) : (
//                 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
//                   {currentRecords.map((rec) => (
//                     <div key={rec.id} className="bg-white/5 border border-white/10 p-5 rounded-2xl space-y-4 hover:border-purple-500/30 transition group">
//                       <div className="flex items-center justify-between">
//                         <div className="flex items-center gap-3">
//                           <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center font-bold border-2 border-white/10 uppercase shrink-0">
//                             {(rec.NAME || "U")[0]}
//                           </div>
//                           <div>
//                             <h4 className="text-sm font-bold truncate max-w-[100px]">{rec.NAME}</h4>
//                             <span className={`inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${rec.status === "draft" ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/20" :
//                               rec.status === "approved" ? "bg-blue-500/20 text-blue-400 border border-blue-500/20" :
//                                 "bg-green-500/20 text-green-400 border border-green-500/20"
//                               }`}>
//                               {rec.status}
//                             </span>
//                           </div>
//                         </div>
//                       </div>

//                       <div className="space-y-2 py-2 border-y border-white/5">
//                         <div className="flex justify-between text-[11px]">
//                           <span className="text-white/70">Gross Salary</span>
//                           <span className="font-medium">₹{Number(rec.gross_salary).toLocaleString()}</span>
//                         </div>
//                         <div className="flex justify-between text-[11px]">
//                           <span className="text-white/70">Deductions</span>
//                           <span className="font-medium text-red-400">-₹{Number(rec.total_deduction).toLocaleString()}</span>
//                         </div>
//                         <div className="flex justify-between text-[11px]">
//                           <span className="text-white/70">Month</span>
//                           <span className="font-medium">{rec.month}</span>
//                         </div>
//                       </div>

//                       <div className="flex justify-between items-center pt-1">
//                         <div>
//                           <p className="text-[10px] text-white/30 uppercase font-bold">Net Salary</p>
//                           <p className="text-lg font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</p>
//                         </div>
//                         <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
//                           {/* 👁 View payslip */}
//                           <button
//                             onClick={() => handleViewPayslip(rec.user_id)}
//                             disabled={isFetchingPayslip}
//                             className="p-2 bg-white/5 hover:bg-purple-500/20 hover:text-purple-300 rounded-lg text-white/70 transition disabled:opacity-50"
//                             title="View Payslip"
//                           >
//                             <Eye size={14} />
//                           </button>
//                           {/* ⬇ Download payslip */}
//                           {/* <button
//                           onClick={() => handleDownloadPayslip(rec.user_id)}
//                           disabled={downloadingId === rec.user_id}
//                           className="p-2 bg-white/5 hover:bg-green-500/20 hover:text-green-300 rounded-lg text-white/70 transition disabled:opacity-50"
//                           title="Download Payslip"
//                         >
//                           {downloadingId === rec.user_id
//                             ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin block" />
//                             : <Download size={14} />}
//                         </button> */}
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )
//             ) : (
//               <div className="col-span-full bg-white/5 border border-white/10 p-10 rounded-2xl text-center">
//                 <History size={32} className="text-white/20 mx-auto mb-3" />
//                 <h3 className="text-sm font-bold text-white mb-1">No Payroll Records Found</h3>
//                 <p className="text-xs text-white/70">Try adjusting your search or processing payroll.</p>
//               </div>
//             )}

//             {/* PAGINATION */}
//             {filteredRecords.length > 0 && (
//               <div className="flex items-center justify-center gap-2 py-4">
//                 <button
//                   onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//                   disabled={currentPage === 1}
//                   className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
//                 >
//                   <ChevronRight size={18} className="rotate-180" />
//                 </button>

//                 {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//                   <button
//                     key={page}
//                     onClick={() => setCurrentPage(page)}
//                     className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
//                       ? 'bg-purple-600 text-white'
//                       : 'bg-white/5 text-white/60 hover:bg-white/10'
//                       }`}
//                   >
//                     {page}
//                   </button>
//                 ))}

//                 <button
//                   onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//                   disabled={currentPage === totalPages}
//                   className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
//                 >
//                   <ChevronRight size={18} />
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* RIGHT SIDEBAR */}
//         <div className="w-full lg:w-80 space-y-6">

//           {/* WORKFLOW */}
//           <div className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-6">
//             <h3 className="text-lg font-bold">Payroll Workflow</h3>
//             <div className="space-y-8 relative">
//               {/* Connector Line */}
//               <div className="absolute left-3.5 top-2 bottom-2 w-0.5 bg-white/10" />

//               {workflow.map((item, i) => (
//                 <div key={i} className="flex gap-4 relative z-10">
//                   <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${item.status === "completed" ? "bg-green-500 text-white" :
//                     item.status === "pending" ? "bg-purple-600 text-white" :
//                       "bg-white/10 text-white/70 border border-white/10"
//                     }`}>
//                     {item.status === "completed" ? <CheckCircle2 size={16} /> : <span className="text-xs font-bold">{item.step || i + 1}</span>}
//                   </div>
//                   <div>
//                     <h4 className={`text-sm font-bold ${item.status === "upcoming" ? "text-white/70" : "text-white"}`}>
//                       {item.label}
//                     </h4>
//                     <p className="text-[11px] text-white/30">{item.sub}</p>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* QUICK REPORTS */}
//           <div className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-6">
//             <h3 className="text-lg font-bold">Quick Reports</h3>
//             <div className="space-y-3">
//               {quickReports.map((report, i) => (
//                 <button
//                   key={i}
//                   onClick={() => report.path && navigate(report.path)}
//                   className="w-full bg-white/5 border border-white/5 p-3 rounded-xl flex items-center justify-between hover:bg-white/10 transition group"
//                 >
//                   <div className="flex items-center gap-3">
//                     <report.icon className="text-white/70 group-hover:text-white/70 transition" size={18} />
//                     <span className="text-xs text-white/70 group-hover:text-white transition">{report.label}</span>
//                   </div>
//                   <ChevronRight size={14} className="text-white/30" />
//                 </button>
//               ))}
//             </div>
//           </div>

//           {/* PAYROLL DISTRIBUTION PIE CHART */}
//           <div className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-4">
//             <h3 className="text-lg font-bold">Payroll Distribution</h3>
//             {totalNet > 0 || totalDeductions > 0 ? (
//               <div className="h-48 w-full">
//                 <ResponsiveContainer width="100%" height="100%">
//                   <PieChart>
//                     <Pie
//                       data={pieData}
//                       cx="50%"
//                       cy="50%"
//                       innerRadius={50}
//                       outerRadius={70}
//                       paddingAngle={5}
//                       dataKey="value"
//                       stroke="none"
//                     >
//                       {pieData.map((entry, index) => (
//                         <Cell key={`cell-${index}`} fill={entry.color} />
//                       ))}
//                     </Pie>
//                     <Tooltip
//                       formatter={(value) => `₹${value.toLocaleString()}`}
//                       contentStyle={{ backgroundColor: '#1B103F', borderColor: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}
//                       itemStyle={{ color: '#fff' }}
//                     />
//                   </PieChart>
//                 </ResponsiveContainer>
//                 <div className="flex justify-center gap-4 mt-2 text-[10px] font-bold uppercase tracking-wider text-white/60">
//                   <div className="flex items-center gap-1.5">
//                     <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" /> Net Salary
//                   </div>
//                   <div className="flex items-center gap-1.5">
//                     <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" /> Deductions
//                   </div>
//                 </div>
//               </div>
//             ) : (
//               <div className="h-48 flex items-center justify-center text-white/30 text-sm">
//                 No payroll data available
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* ALREADY EXISTS MODAL */}
//       {showExistsModal && (
//         <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
//           <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowExistsModal(false)} />
//           <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-md rounded-3xl p-8 shadow-2xl space-y-6 text-center transform transition-all">
//             <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
//               <AlertCircle size={40} className="text-yellow-500" />
//             </div>
//             <div className="space-y-2">
//               <h3 className="text-2xl font-bold text-white">Payroll Already Processed</h3>
//               <p className="text-white/80 text-sm leading-relaxed">
//                 Payroll for <span className="text-white font-bold">{new Date(fromMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span> has already been generated and processed.
//               </p>
//             </div>
//             <div className="flex flex-col gap-3 pt-4">
//               <button
//                 onClick={() => setShowExistsModal(false)}
//                 className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold transition border border-white/10"
//               >
//                 Close
//               </button>
//               <button
//                 onClick={() => {
//                   setShowExistsModal(false);
//                   navigate("/payroll/records");
//                 }}
//                 className="w-full text-purple-400 text-sm font-bold hover:text-purple-300 transition"
//               >
//                 View Processed Records
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* PAYSLIP MODAL */}
//       <PayslipModal
//         isOpen={isPayslipModalOpen}
//         onClose={() => setIsPayslipModalOpen(false)}
//         data={payslipData}
//       />
//     </div>
//   );
// };

// export default Payroll;
